// Centralized environment variable access for Vite
// Vite exposes only variables starting with VITE_ via import.meta.env
// Provide defaults matching former CRA fallbacks

// Type for runtime config injected via docker-entrypoint.sh
interface RuntimeConfig {
  VITE_OIDC_AUTHORITY?: string;
  VITE_OIDC_CLIENT_ID?: string;
  VITE_OIDC_REDIRECT_URI?: string;
  VITE_OIDC_POST_LOGOUT_REDIRECT_URI?: string;
  VITE_OIDC_SCOPE?: string;
  VITE_API_BASE_URL?: string;
  VITE_ADMIN_GROUP?: string;
  VITE_USER_GROUP?: string;
  VITE_USE_DIRECT_API?: string;
  VITE_SKIP_OIDC_PROBE?: string;
}

declare global {
  interface Window {
    APP_CONFIG?: RuntimeConfig;
  }
}

// Helper to get config value: runtime config takes precedence over build-time env
const getConfig = (key: keyof RuntimeConfig, fallback: string): string => {
  const runtimeConfig = (globalThis as typeof globalThis & { APP_CONFIG?: RuntimeConfig }).APP_CONFIG;
  return runtimeConfig?.[key] || import.meta.env[key] || fallback;
};

export const ENV = {
  OIDC_AUTHORITY: getConfig('VITE_OIDC_AUTHORITY', 'https://ping-sso-uat.schneider-electric.com'),
  OIDC_CLIENT_ID: getConfig('VITE_OIDC_CLIENT_ID', 'smartalarm_dev_50590'),
  OIDC_REDIRECT_URI: getConfig('VITE_OIDC_REDIRECT_URI', `${globalThis.location.origin}/callback`),
  OIDC_POST_LOGOUT_REDIRECT_URI: getConfig('VITE_OIDC_POST_LOGOUT_REDIRECT_URI', `${globalThis.location.origin}/login`),
  OIDC_SCOPE: getConfig('VITE_OIDC_SCOPE', 'openid profile groups'),
  ADMIN_GROUP: getConfig('VITE_ADMIN_GROUP', 'your-admin-group-name'),
  USER_GROUP: getConfig('VITE_USER_GROUP', 'your-user-group-name'),
  API_BASE_URL: getConfig('VITE_API_BASE_URL', 'https://localhost:7061'),

  // SignalR hub URL for real-time bureau alarm notifications
  SIGNALR_HUB_URL: import.meta.env.VITE_SIGNALR_HUB_URL || `${getConfig('VITE_API_BASE_URL', 'https://localhost:7061')}/hubs/csh`,

  // access | id — choose which OIDC token to send in Authorization header
  API_TOKEN_SOURCE: (import.meta.env.VITE_API_TOKEN_SOURCE as 'access' | 'id') || 'access',

  // If true, axios will call API_BASE_URL directly; if false (default), we rely on Vite dev proxy and use relative '/api' paths
  // Read these from runtime `window.APP_CONFIG` first (via getConfig) so the container runtime can flip behaviour
  // without requiring a rebuild. Fall back to build-time import.meta.env when runtime value is absent.
  USE_DIRECT_API: (getConfig('VITE_USE_DIRECT_API', import.meta.env.VITE_USE_DIRECT_API || 'false') === 'true'),
  SKIP_OIDC_PROBE: (getConfig('VITE_SKIP_OIDC_PROBE', import.meta.env.VITE_SKIP_OIDC_PROBE || 'false') === 'true'),

  // Session inactivity timeout (minutes). Set to 0 or negative to disable.
  SESSION_TIMEOUT_MINUTES: Number(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || 30),

  // Optional warning lead time (seconds before timeout to show modal / message if implemented later)
  SESSION_TIMEOUT_WARNING_SECONDS: Number(import.meta.env.VITE_SESSION_TIMEOUT_WARNING_SECONDS || 0)
} as const;

export type EnvKeys = keyof typeof ENV;
